import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import moment from "moment";
import { getAllCourts, deleteCourt } from "../../api/courts";
import { ICourt } from "../../models/ICourt";
import { useAppDispatch } from "../../store/store";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import CourtDialog from "../CourtDialog";
import DeleteCourtDialog from "../DeleteCourtDialog";
import { Button, Card, EmptyState, IconButton, Loader, Tag } from "../../ui";
import "./styles.scss";

type Props = {
  canManage: boolean;
};

const CourtsManagement = ({ canManage }: Props) => {
  const dispatch = useAppDispatch();
  const [courts, setCourts] = useState<ICourt[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCourtDialogOpen, setIsCourtDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCourt, setSelectedCourt] = useState<ICourt | undefined>();
  const [courtToDelete, setCourtToDelete] = useState<ICourt | undefined>();

  const fetchCourts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getAllCourts();
      setCourts(data);
    } catch (err: any) {
      if (err?.message === "No courts found") setCourts([]);
      else dispatch(setGeneralAlertMessage({ message: err?.message || "Failed to load courts", type: "error" }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchCourts();
  }, [fetchCourts]);

  const handleCreate = () => {
    setSelectedCourt(undefined);
    setIsCourtDialogOpen(true);
  };

  const handleEdit = (court: ICourt) => {
    setSelectedCourt(court);
    setIsCourtDialogOpen(true);
  };

  const handleDelete = (court: ICourt) => {
    setCourtToDelete(court);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!courtToDelete) return;
    try {
      await deleteCourt(courtToDelete.id);
      dispatch(setGeneralAlertMessage({ message: "Court deleted successfully", type: "success" }));
      setCourtToDelete(undefined);
      await fetchCourts();
    } catch (error: any) {
      dispatch(setGeneralAlertMessage({ message: error?.message || "Failed to delete court", type: "error" }));
      throw error;
    }
  };

  const handleCourtDialogClose = async (update?: boolean) => {
    setIsCourtDialogOpen(false);
    setSelectedCourt(undefined);
    if (update) await fetchCourts();
  };

  const statusTag = (court: ICourt) =>
    court.isActive ? <Tag tone="ok">Active</Tag> : <Tag tone="neutral">Inactive</Tag>;

  return (
    <Card
      className="courts-management"
      title="Courts"
      subtitle={`${courts.length} ${courts.length === 1 ? "court" : "courts"}`}
      padding="none"
      actions={
        canManage && (
          <Button icon={<Plus size={16} />} onClick={handleCreate}>
            Add Court
          </Button>
        )
      }
    >
      {loading && courts.length === 0 ? (
        <Loader />
      ) : courts.length === 0 ? (
        <EmptyState compact title="No courts found." description={canManage && "Create one to get started."} />
      ) : (
        <>
          {/* Desktop table */}
          <div className="courts-table-container">
            <table className="ui-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th className="ui-table__num">Reservations</th>
                  <th>Created</th>
                  {canManage && <th className="ui-table__actions">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {courts.map((court) => (
                  <tr key={court.id}>
                    <td className="ui-table__primary">{court.name}</td>
                    <td className="courts-table__muted">{court.description || "-"}</td>
                    <td>{statusTag(court)}</td>
                    <td className="ui-table__num">{court.reservationCount || 0}</td>
                    <td>{moment(court.createdAt).format("DD MMM, YYYY")}</td>
                    {canManage && (
                      <td className="ui-table__actions">
                        <IconButton label="Edit" icon={<Pencil size={15} />} onClick={() => handleEdit(court)} />
                        <IconButton
                          label="Delete"
                          variant="danger"
                          icon={<Trash2 size={15} />}
                          onClick={() => handleDelete(court)}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="courts-cards">
            {courts.map((court) => (
              <div key={court.id} className="court-card">
                <div className="court-card-header">
                  <div className="court-card-name">{court.name}</div>
                  {statusTag(court)}
                </div>
                {court.description && <div className="court-card-description">{court.description}</div>}
                <div className="court-card-meta">
                  <span><strong>{court.reservationCount || 0}</strong> reservations</span>
                  <span>Created {moment(court.createdAt).format("DD MMM, YYYY")}</span>
                </div>
                {canManage && (
                  <div className="court-card-actions">
                    <Button size="sm" variant="ghost" icon={<Pencil size={14} />} onClick={() => handleEdit(court)}>
                      Edit
                    </Button>
                    <Button size="sm" variant="danger-tint" icon={<Trash2 size={14} />} onClick={() => handleDelete(court)}>
                      Delete
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {isCourtDialogOpen && (
        <CourtDialog court={selectedCourt} isOpen={isCourtDialogOpen} onClose={handleCourtDialogClose} />
      )}

      {isDeleteDialogOpen && courtToDelete && (
        <DeleteCourtDialog
          courtName={courtToDelete.name}
          isOpen={isDeleteDialogOpen}
          onDelete={handleDeleteConfirm}
          onClose={(update) => {
            setIsDeleteDialogOpen(false);
            if (update) setCourtToDelete(undefined);
          }}
        />
      )}
    </Card>
  );
};

export default CourtsManagement;
